import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { SheetHeader, SheetTitle, SheetClose } from './ui/sheet';
import CountdownTimer from './CountdownTimer';

export const CartSidebar = () => {
  const { t } = useLanguage();
  const { items, updateQuantity, removeItem, getTotal, loading } = useCart();
  const navigate = useNavigate();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID').format(price);
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  const handleUpdateQuantity = async (item, newQty) => {
    await updateQuantity(item.product_id, item.store_id, newQty);
  };

  const handleRemoveItem = async (item) => {
    await removeItem(item.product_id, item.store_id);
  };

  return (
    <div className="flex flex-col h-full" data-testid="cart-sidebar">
      <SheetHeader className="px-6 py-4 border-b border-gray-100">
        <SheetTitle className="flex items-center gap-2 font-heading">
          <ShoppingBag className="w-5 h-5 text-[#0052CC]" />
          {t('cart')} ({items.length} {t('items')})
          {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </SheetTitle>
      </SheetHeader>

      {items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <ShoppingBag className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="font-medium text-gray-900 mb-1">{t('cart_empty')}</h3>
          <p className="text-sm text-gray-500">{t('cart_empty_desc')}</p>
          <SheetClose asChild>
            <Button className="mt-6 bg-[#0052CC] hover:bg-[#0043A8] rounded-full" data-testid="continue-shopping-btn">
              {t('continue_shopping')}
            </Button>
          </SheetClose>
        </div>
      ) : (
        <>
          <ScrollArea className="flex-1 px-6">
            <div className="py-4 space-y-4">
              {items.map((item) => (
                <div
                  key={`${item.product_id}-${item.store_id}`}
                  className="flex gap-3 p-3 bg-gray-50 rounded-xl"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-white flex-shrink-0">
                    <img
                      src={item.img_url || 'https://via.placeholder.com/80x80?text=No+Image'}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                      {item.product_name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">{item.store_name}</p>
                    <p className="text-[#0052CC] font-bold mt-1">
                      Rp{formatPrice(item.price)}
                    </p>

                    {/* Countdown Timer */}
                    {item.expired_time && (
                      <div className="mt-2">
                        <CountdownTimer
                          expiredTime={item.expired_time}
                          onExpire={() => handleRemoveItem(item)}
                        />
                      </div>
                    )}

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                          className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors"
                          data-testid={`decrease-${item.product_id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center font-medium text-sm" data-testid={`quantity-${item.product_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition-colors"
                          data-testid={`increase-${item.product_id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => handleRemoveItem(item)}
                        className="text-red-500 hover:text-red-600 p-1"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="border-t border-gray-100 px-6 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">{t('subtotal')}</span>
              <span className="text-xl font-bold text-gray-900" data-testid="cart-total">
                Rp{formatPrice(getTotal())}
              </span>
            </div>
            <SheetClose asChild>
              <Button
                onClick={handleCheckout}
                className="w-full bg-[#FF9900] hover:bg-[#E68A00] text-white rounded-lg py-3 font-bold text-base"
                data-testid="checkout-btn"
              >
                {t('checkout')}
              </Button>
            </SheetClose>
          </div>
        </>
      )}
    </div>
  );
};

export default CartSidebar;
